import { CheckCircle2, Link2Off, RefreshCw, ShieldAlert } from 'lucide-react'
import { startOAuthOrReport } from '@/api/endpoints/oauth'
import { toast } from 'sonner'

import { useDisconnectTiktok, useTiktokProfile, useTiktokUploadStatus } from '@/api/endpoints/tiktok/uploads'
import type { Channel } from '@/api/endpoints/channels'
import { Button } from '@/components/ui/Button'
import { Chip } from '@/components/ui/Chip'
import { Section } from '@/components/ds/Section'
import { errorMessage } from '@/lib/errors'
import styles from './TiktokOAuthSection.module.scss'

interface TiktokOAuthSectionProps {
  channel: Channel
}

/**
 * TikTok OAuth controls. Mirrors :class:`YoutubeOAuthSection` — the
 * status query polls ``GET /api/v1/uploads/tiktok/status`` (15s) and
 * flips the chip between "Conectado" / "Desconectado" / "Sem
 * client_secret" without a manual refresh.
 *
 * When connected, the profile counters (display name + follower /
 * video / likes count) render under the chip — that's what
 * demonstrates the ``user.info.*`` Login Kit scopes during TikTok
 * app review.
 */
export function TiktokOAuthSection({ channel }: TiktokOAuthSectionProps) {
  const status = useTiktokUploadStatus(channel.slug)
  const profile = useTiktokProfile(channel.slug)
  const disconnect = useDisconnectTiktok()

  const auth = status.data?.auth
  const connected = auth?.is_authenticated === true
  const configured = auth?.is_configured === true
  const profileData = profile.data

  return (
    <Section
      title="Conexão TikTok"
      description="OAuth pra esse perfil. Em sandbox o app TikTok limita uploads ao Inbox (drafts) — direct posts só após audit."
    >
      <div className={styles.statusRow}>
        {status.isPending ? (
          <Chip>verificando...</Chip>
        ) : status.isError ? (
          <Chip variant="danger">
            <ShieldAlert aria-hidden style={{ width: 12, height: 12, marginRight: 4 }} />
            erro
          </Chip>
        ) : !configured ? (
          <Chip variant="warning">Sem TIKTOK_CLIENT_KEY</Chip>
        ) : connected ? (
          <Chip variant="success">
            <CheckCircle2 aria-hidden style={{ width: 12, height: 12, marginRight: 4 }} />
            Conectado
          </Chip>
        ) : (
          <Chip variant="warning">Desconectado</Chip>
        )}

        <div className={styles.statusActions}>
          <Button
            size="sm"
            variant={connected ? 'secondary' : 'primary'}
            leadingIcon={<RefreshCw />}
            onClick={() => {
              startOAuthOrReport('tiktok', channel.slug, (m) => toast.error(m))
            }}
            disabled={!configured}
          >
            {connected ? 'Reconectar' : 'Conectar'}
          </Button>
          {connected ? (
            <Button
              size="sm"
              variant="ghost"
              leadingIcon={<Link2Off />}
              disabled={disconnect.isPending}
              onClick={() => {
                disconnect.mutate(channel.slug, {
                  onSuccess: () => {
                    toast.success(`${channel.name} desconectado do TikTok`)
                  },
                  onError: (err) => {
                    toast.error(errorMessage(err, 'Falha ao desconectar.'))
                  },
                })
              }}
            >
              Desconectar
            </Button>
          ) : null}
        </div>
      </div>

      {connected && profileData?.connected ? (
        <div className={styles.profileRow}>
          <span className={styles.profileItem}>
            <strong>{profileData.display_name || '—'}</strong>
            {profileData.is_verified ? <span aria-label="verified">✓</span> : null}
          </span>
          <span className={styles.profileItem}>
            <strong>{profileData.follower_count.toLocaleString('pt-BR')}</strong>
            <span>seguidores</span>
          </span>
          <span className={styles.profileItem}>
            <strong>{profileData.video_count.toLocaleString('pt-BR')}</strong>
            <span>vídeos</span>
          </span>
          <span className={styles.profileItem}>
            <strong>{profileData.likes_count.toLocaleString('pt-BR')}</strong>
            <span>curtidas</span>
          </span>
        </div>
      ) : null}

      {!configured ? (
        <p className={styles.note}>
          Preencha <code>TIKTOK_CLIENT_KEY</code> e <code>TIKTOK_CLIENT_SECRET</code> no <code>.env</code> e
          recrie o backend (<code>make dev-tunnel</code>) pra habilitar OAuth.
        </p>
      ) : null}
    </Section>
  )
}
